"use client";

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { joomlaVersions } from './joomla-version';
import { 
  Search, 
  ArrowUpDown, 
  ChevronDown, 
  CheckCircle, 
  XCircle, 
  Calendar, 
  AlertTriangle,
  Download,
  FileText,
  FileJson,
  Loader2,
  FileTextIcon
} from "lucide-react";

// Import components
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";

const JoomlaVersionTable = () => {
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    majorVersion: 'all',
    yearRange: 'all',
    supportDuration: 'all'
  });
  const [sortConfig, setSortConfig] = useState({
    key: 'version',
    direction: 'ascending'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState('csv');
  const [exportProgress, setExportProgress] = useState(0);
  const [exporting, setExporting] = useState(false);
  const rowsPerPage = 20;
  
  const tableRef = useRef(null);

  useEffect(() => {
    // Simulate loading of data
    setTimeout(() => {
      setLoading(false);
    }, 500);
  }, []);

  // Format date in human-readable format
  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === "TBD") return "TBD";
    
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Calculate support duration in days and format it
  const calculateSupportDuration = (releaseDate, eolDate) => {
    if (releaseDate === "TBD" || eolDate === "TBD") return "Unknown";
    
    const release = new Date(releaseDate);
    const eol = new Date(eolDate);
    const diffTime = eol - release;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    // Convert to years and months for readability
    const years = Math.floor(diffDays / 365);
    const months = Math.floor((diffDays % 365) / 30);
    
    if (years > 0 && months > 0) {
      return `${years} year${years !== 1 ? 's' : ''}, ${months} month${months !== 1 ? 's' : ''}`;
    } else if (years > 0) {
      return `${years} year${years !== 1 ? 's' : ''}`;
    } else if (months > 0) {
      return `${months} month${months !== 1 ? 's' : ''}`;
    } else {
      return `${diffDays} days`;
    }
  };

  // Get unique major versions for filter
  const majorVersions = useMemo(() => {
    const versions = [...new Set(joomlaVersions.map(item => item.version.split('.')[0]))];
    return versions.sort((a, b) => parseInt(a) - parseInt(b));
  }, []);

  // Get unique release years for filter
  const releaseYears = useMemo(() => {
    const years = [...new Set(joomlaVersions
      .filter(item => item.release_date !== "TBD")
      .map(item => new Date(item.release_date).getFullYear())
    )];
    return years.sort();
  }, []);

  // Handle sorting
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Apply all filters and sorting
  const filteredAndSortedData = useMemo(() => {
    let filteredData = [...joomlaVersions];
    
    // Apply search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filteredData = filteredData.filter(item => 
        item.version.toLowerCase().includes(query) ||
        (item.release_date && item.release_date.toLowerCase().includes(query)) ||
        (item.eol_date && item.eol_date.toLowerCase().includes(query)) ||
        item.status.toLowerCase().includes(query)
      );
    }
    
    // Apply status filter
    if (filters.status !== 'all') {
      filteredData = filteredData.filter(item => item.status === filters.status);
    }
    
    // Apply major version filter
    if (filters.majorVersion !== 'all') {
      filteredData = filteredData.filter(item => 
        item.version.startsWith(filters.majorVersion + '.')
      );
    }
    
    // Apply year range filter
    if (filters.yearRange !== 'all' && filters.yearRange !== '') {
      const year = parseInt(filters.yearRange);
      filteredData = filteredData.filter(item => 
        item.release_date !== "TBD" && 
        new Date(item.release_date).getFullYear() === year
      );
    }
    
    // Apply support duration filter
    if (filters.supportDuration !== 'all') {
      filteredData = filteredData.filter(item => {
        if (item.release_date === "TBD" || item.eol_date === "TBD") return false;
        
        const release = new Date(item.release_date);
        const eol = new Date(item.eol_date);
        const diffTime = eol - release;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (filters.supportDuration === 'lessThan1Year') {
          return diffDays < 365;
        } else if (filters.supportDuration === '1To2Years') {
          return diffDays >= 365 && diffDays < 730;
        } else if (filters.supportDuration === '2To3Years') {
          return diffDays >= 730 && diffDays < 1095;
        } else if (filters.supportDuration === 'moreThan3Years') {
          return diffDays >= 1095;
        }
        
        return true;
      });
    }
    
    // Apply sorting
    if (sortConfig.key) {
      filteredData.sort((a, b) => {
        // Special case for version sorting
        if (sortConfig.key === 'version') {
          const versionA = a.version.split('.').map(num => parseInt(num, 10));
          const versionB = b.version.split('.').map(num => parseInt(num, 10));
          
          for (let i = 0; i < Math.max(versionA.length, versionB.length); i++) {
            const numA = versionA[i] || 0;
            const numB = versionB[i] || 0;
            if (numA !== numB) {
              return sortConfig.direction === 'ascending' ? numA - numB : numB - numA;
            }
          }
          return 0;
        }
        
        // Special case for date sorting
        if (sortConfig.key === 'release_date' || sortConfig.key === 'eol_date') {
          if (a[sortConfig.key] === "TBD") return sortConfig.direction === 'ascending' ? 1 : -1;
          if (b[sortConfig.key] === "TBD") return sortConfig.direction === 'ascending' ? -1 : 1;
          
          const dateA = new Date(a[sortConfig.key]);
          const dateB = new Date(b[sortConfig.key]);
          
          return sortConfig.direction === 'ascending' ? dateA - dateB : dateB - dateA;
        }
        
        // Default sorting for other fields
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    
    return filteredData;
  }, [searchQuery, filters, sortConfig]);

  // Paginate data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return filteredAndSortedData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredAndSortedData, currentPage, rowsPerPage]);

  // Calculate total pages
  const totalPages = Math.ceil(filteredAndSortedData.length / rowsPerPage);

  // Handle filter changes
  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Handle search input
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page when search changes
  };

  // Reset all filters and search
  const resetFilters = () => {
    setFilters({
      status: 'all',
      majorVersion: 'all',
      yearRange: 'all',
      supportDuration: 'all'
    });
    setSearchQuery('');
    setCurrentPage(1);
  };

  // Export data to CSV
  const exportToCSV = async () => {
    try {
      setExporting(true);
      setExportProgress(10);
      
      // Create CSV content
      const headers = ['Version', 'Release Date', 'EOL Date', 'Status', 'Support Duration'];
      let csvContent = headers.join(',') + '\n';
      
      // Process data in chunks to avoid blocking the UI
      const chunkSize = 50;
      const totalChunks = Math.ceil(filteredAndSortedData.length / chunkSize);
      
      for (let i = 0; i < totalChunks; i++) {
        const chunk = filteredAndSortedData.slice(i * chunkSize, (i + 1) * chunkSize);
        
        // Process each row in the chunk
        chunk.forEach(item => {
          const supportDuration = calculateSupportDuration(item.release_date, item.eol_date);
          const values = [
            item.version,
            item.release_date, 
            item.eol_date, 
            item.status,
            supportDuration
          ].map(value => {
            // Escape commas and quotes
            if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
              return `"${value.replace(/"/g, '""')}"`;
            }
            return value;
          });
          
          csvContent += values.join(',') + '\n';
        });
        
        // Update progress
        setExportProgress(10 + Math.floor((i + 1) / totalChunks * 70));
        
        // Allow UI to update
        await new Promise(resolve => setTimeout(resolve, 0));
      }
      
      setExportProgress(80);
      
      // Create blob and trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `joomla-versions-${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setExportProgress(100);
      
      // Clean up
      setTimeout(() => {
        setExportProgress(0);
        setExporting(false);
        setExportOpen(false);
      }, 1000);
      
    } catch (error) {
      console.error('Error exporting to CSV:', error);
      setExporting(false);
      setExportProgress(0);
    }
  };

  // Export data to JSON
  const exportToJSON = async () => {
    try {
      setExporting(true);
      setExportProgress(10);
      
      // Process data in chunks
      const chunkSize = 50;
      const totalChunks = Math.ceil(filteredAndSortedData.length / chunkSize);
      const exportData = [];
      
      for (let i = 0; i < totalChunks; i++) {
        const chunk = filteredAndSortedData.slice(i * chunkSize, (i + 1) * chunkSize);
        
        // Process each row in the chunk
        chunk.forEach(item => {
          exportData.push({
            ...item,
            supportDuration: calculateSupportDuration(item.release_date, item.eol_date),
            releaseDateFormatted: formatDate(item.release_date),
            eolDateFormatted: formatDate(item.eol_date)
          });
        });
        
        // Update progress
        setExportProgress(10 + Math.floor((i + 1) / totalChunks * 70));
        
        // Allow UI to update
        await new Promise(resolve => setTimeout(resolve, 0));
      }
      
      setExportProgress(80);
      
      // Create JSON blob and trigger download
      const jsonContent = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `joomla-versions-${new Date().toISOString().slice(0, 10)}.json`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setExportProgress(100);
      
      // Clean up
      setTimeout(() => {
        setExportProgress(0);
        setExporting(false);
        setExportOpen(false);
      }, 1000);
      
    } catch (error) {
      console.error('Error exporting to JSON:', error);
      setExporting(false);
      setExportProgress(0);
    }
  };

  // Export as PDF
  const exportToPDF = async () => {
    try {
      setExporting(true);
      setExportProgress(10);
      
      // Prepare content for PDF
      const title = `Joomla Versions - ${new Date().toLocaleDateString()}`;
      const tableHeader = `
        <tr>
          <th style="text-align:left; padding:8px; border-bottom:1px solid #ddd;">Version</th>
          <th style="text-align:left; padding:8px; border-bottom:1px solid #ddd;">Release Date</th>
          <th style="text-align:left; padding:8px; border-bottom:1px solid #ddd;">EOL Date</th>
          <th style="text-align:left; padding:8px; border-bottom:1px solid #ddd;">Status</th>
          <th style="text-align:left; padding:8px; border-bottom:1px solid #ddd;">Support Duration</th>
        </tr>
      `;
      
      let tableRows = '';
      
      // Process data in chunks
      const chunkSize = 50;
      const totalChunks = Math.ceil(filteredAndSortedData.length / chunkSize);
      
      for (let i = 0; i < totalChunks; i++) {
        const chunk = filteredAndSortedData.slice(i * chunkSize, (i + 1) * chunkSize);
        
        // Process each row in the chunk
        chunk.forEach((item, index) => {
          const bgColor = index % 2 === 0 ? '#f9f9f9' : '#ffffff';
          const statusColor = item.status === 'Active' ? '#10b981' : 
                              item.status === 'EOL' ? '#ef4444' : '#6b7280';
          
          tableRows += `
            <tr style="background-color:${bgColor};">
              <td style="text-align:left; padding:8px; border-bottom:1px solid #ddd;">${item.version}</td>
              <td style="text-align:left; padding:8px; border-bottom:1px solid #ddd;">${formatDate(item.release_date)}</td>
              <td style="text-align:left; padding:8px; border-bottom:1px solid #ddd;">${formatDate(item.eol_date)}</td>
              <td style="text-align:left; padding:8px; border-bottom:1px solid #ddd; color:${statusColor};">${item.status}</td>
              <td style="text-align:left; padding:8px; border-bottom:1px solid #ddd;">${calculateSupportDuration(item.release_date, item.eol_date)}</td>
            </tr>
          `;
        });
        
        // Update progress
        setExportProgress(10 + Math.floor((i + 1) / totalChunks * 70));
        
        // Allow UI to update
        await new Promise(resolve => setTimeout(resolve, 0));
      }
      
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>${title}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #1a365d; }
            table { border-collapse: collapse; width: 100%; margin-top: 20px; }
            .filters { margin: 20px 0; padding: 10px; background-color: #f1f5f9; border-radius: 4px; }
          </style>
        </head>
        <body>
          <h1>${title}</h1>
          <p>Generated on ${new Date().toLocaleString()}</p>
          <div class="filters">
            <strong>Filters applied:</strong> 
            Status: ${filters.status === 'all' ? 'All' : filters.status}, 
            Major Version: ${filters.majorVersion === 'all' ? 'All' : filters.majorVersion}, 
            Release Year: ${filters.yearRange === 'all' ? 'All' : filters.yearRange},
            Support Duration: ${filters.supportDuration === 'all' ? 'All' : filters.supportDuration}
          </div>
          <p><strong>Total results:</strong> ${filteredAndSortedData.length}</p>
          <table>
            <thead>${tableHeader}</thead>
            <tbody>${tableRows}</tbody>
          </table>
        </body>
        </html>
      `;
      
      setExportProgress(80);
      
      // Create a blob URL for the HTML content
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      
      // Open in a new window for printing to PDF
      const printWindow = window.open(url, '_blank');
      
      if (printWindow) {
        setExportProgress(90);
        
        // Attach onload event to print after content is loaded
        printWindow.onload = function() {
          printWindow.print();
          setExportProgress(100);
          
          // Clean up
          setTimeout(() => {
            setExportProgress(0);
            setExporting(false);
            setExportOpen(false);
          }, 1000);
        };
      } else {
        // Handle if popup is blocked
        setExportProgress(0);
        setExporting(false);
        alert('Please enable popups to export as PDF');
      }
      
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      setExporting(false);
      setExportProgress(0);
    }
  };

  // Handle export based on selected format
  const handleExport = () => {
    if (exporting) return;
    
    switch (exportFormat) {
      case 'csv':
        exportToCSV();
        break;
      case 'json':
        exportToJSON();
        break;
      case 'pdf':
        exportToPDF();
        break;
      default:
        exportToCSV();
    }
  };

  // Render status badges
  const getStatusBadge = (status) => {
    switch(status) {
      case 'Active':
        return (
          <Badge variant="success" className="flex items-center gap-1">
            <CheckCircle className="w-3 h-3" />
            Active
          </Badge>
        );
      case 'EOL':
        return (
          <Badge variant="destructive" className="flex items-center gap-1">
            <XCircle className="w-3 h-3" />
            EOL
          </Badge>
        );
      case 'Upcoming':
        return (
          <Badge variant="secondary" className="flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            Upcoming
          </Badge>
        );
      default:
        return (
          <Badge variant="outline">{status}</Badge>
        );
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Joomla Version Database</CardTitle>
          <CardDescription>Loading version information...</CardDescription>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="mx-auto max-w-7xl">
    <Card className='border-none shadow-none'>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-4">
          
    
          <Dialog open={exportOpen} onOpenChange={setExportOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2">
                <Download className="h-4 w-4" />
                Export Data
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Export Version Data</DialogTitle>
                <DialogDescription>
                  Choose a format to export the currently filtered data.
                </DialogDescription>
              </DialogHeader>
              
              <div className="grid gap-4 py-4">
                <div className="space-y-2">
                  <h4 className="font-medium">Export Format</h4>
                  <div className="grid grid-cols-3 gap-2">
                    <Button 
                      variant={exportFormat === 'csv' ? "default" : "outline"} 
                      className="justify-start gap-2"
                      onClick={() => setExportFormat('csv')}
                    >
                      <FileTextIcon className="h-4 w-4" />
                      CSV
                    </Button>
                    <Button 
                      variant={exportFormat === 'json' ? "default" : "outline"} 
                      className="justify-start gap-2"
                      onClick={() => setExportFormat('json')}
                    >
                      <FileJson className="h-4 w-4" />
                      JSON
                    </Button>
                    <Button 
                      variant={exportFormat === 'pdf' ? "default" : "outline"} 
                      className="justify-start gap-2"
                      onClick={() => setExportFormat('pdf')}
                    >
                      <FileText className="h-4 w-4" />
                      PDF
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <h4 className="font-medium">Selected Data</h4>
                  <p className="text-sm text-muted-foreground">
                    {filteredAndSortedData.length} versions will be exported.
                  </p>
                </div>
                
                {exportProgress > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-medium">Export Progress</h4>
                    <Progress value={exportProgress} className="h-2" />
                  </div>
                )}
                
                <Button 
                  onClick={handleExport} 
                  disabled={exporting} 
                  className="w-full gap-2"
                >
                  {exporting && <Loader2 className="h-4 w-4 animate-spin" />}
                  {exporting ? "Exporting..." : "Export Now"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      
      <CardContent>
        {/* Search and filters */}
        <div className="space-y-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search bar */}
            <div className="relative flex-grow max-w-md">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-muted-foreground" />
              </div>
              <Input
                type="text"
                className="pl-9"
                placeholder="Search versions..."
                value={searchQuery}
                onChange={handleSearchChange}
              />
            </div>
            
            {/* Reset filters button */}
            <Button
              variant="outline"
              size="sm"
              onClick={resetFilters}
            >
              Reset Filters
            </Button>
            
            <div className="ml-auto text-sm text-muted-foreground">
              {filteredAndSortedData.length} versions found
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Status filter */}
            <div className="space-y-2">
              <label htmlFor="status-filter" className="text-sm font-medium">Status</label>
              <Select 
                value={filters.status} 
                onValueChange={(value) => handleFilterChange('status', value)}
              >
                <SelectTrigger id="status-filter">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="EOL">End of Life</SelectItem>
                  <SelectItem value="Upcoming">Upcoming</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Major version filter */}
            <div className="space-y-2">
              <label htmlFor="major-version-filter" className="text-sm font-medium">Major Version</label>
              <Select 
                value={filters.majorVersion} 
                onValueChange={(value) => handleFilterChange('majorVersion', value)}
              >
                <SelectTrigger id="major-version-filter">
                  <SelectValue placeholder="All Versions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Versions</SelectItem>
                  {majorVersions.map(version => (
                    <SelectItem key={version} value={version}>Joomla {version}.x</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Year range filter */}
            <div className="space-y-2">
              <label htmlFor="year-filter" className="text-sm font-medium">Release Year</label>
              <Select 
                value={filters.yearRange} 
                onValueChange={(value) => handleFilterChange('yearRange', value)}
              >
                <SelectTrigger id="year-filter">
                  <SelectValue placeholder="All Years" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Years</SelectItem>
                  {releaseYears.map(year => (
                    <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Support duration filter */}
            <div className="space-y-2">
              <label htmlFor="support-filter" className="text-sm font-medium">Support Duration</label>
              <Select 
                value={filters.supportDuration} 
                onValueChange={(value) => handleFilterChange('supportDuration', value)}
              >
                <SelectTrigger id="support-filter">
                  <SelectValue placeholder="All Durations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Durations</SelectItem>
                  <SelectItem value="lessThan1Year">Less than 1 year</SelectItem>
                  <SelectItem value="1To2Years">1-2 years</SelectItem>
                  <SelectItem value="2To3Years">2-3 years</SelectItem>
                  <SelectItem value="moreThan3Years">More than 3 years</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        
        {/* Table */}
        <div className="rounded-md border" ref={tableRef}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[120px]">
                  <div className="flex items-center gap-1 cursor-pointer" onClick={() => requestSort('version')}>
                    Version
                    {sortConfig.key === 'version' ? (
                      sortConfig.direction === 'ascending' ? 
                        <ChevronDown className="h-4 w-4 rotate-180"/> : 
                        <ChevronDown className="h-4 w-4" />) : (
                            <ArrowUpDown className="h-4 w-4" />
                          )}
                        </div>
                      </TableHead>
                      <TableHead>
                        <div className="flex items-center gap-1 cursor-pointer" onClick={() => requestSort('release_date')}>
                          Release Date
                          {sortConfig.key === 'release_date' ? (
                            sortConfig.direction === 'ascending' ? 
                              <ChevronDown className="h-4 w-4 rotate-180"/> : 
                              <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4" />
                          )}
                        </div>
                      </TableHead>
                      <TableHead>
                        <div className="flex items-center gap-1 cursor-pointer" onClick={() => requestSort('eol_date')}>
                          EOL Date
                          {sortConfig.key === 'eol_date' ? (
                            sortConfig.direction === 'ascending' ? 
                              <ChevronDown className="h-4 w-4 rotate-180"/> : 
                              <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4" />
                          )}
                        </div>
                      </TableHead>
                      <TableHead className="w-[120px]">
                        <div className="flex items-center gap-1 cursor-pointer" onClick={() => requestSort('status')}>
                          Status
                          {sortConfig.key === 'status' ? (
                            sortConfig.direction === 'ascending' ? 
                              <ChevronDown className="h-4 w-4 rotate-180"/> : 
                              <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4" />
                          )}
                        </div>
                      </TableHead>
                      <TableHead>Support Duration</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedData.length > 0 ? (
                      paginatedData.map((version) => (
                        <TableRow key={version.version}>
                          <TableCell className="font-medium">{version.version}</TableCell>
                          <TableCell>{formatDate(version.release_date)}</TableCell>
                          <TableCell>{formatDate(version.eol_date)}</TableCell>
                          <TableCell>{getStatusBadge(version.status)}</TableCell>
                          <TableCell>{calculateSupportDuration(version.release_date, version.eol_date)}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} className="h-32 text-center">
                          <div className="flex flex-col items-center justify-center py-4">
                            <AlertTriangle className="h-8 w-8 text-amber-500 mb-2" />
                            <p className="font-medium">No versions found matching your filters</p>
                            <p className="text-muted-foreground mt-1">Try adjusting your search criteria or reset the filters</p>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between py-4">
                  <div className="text-sm text-muted-foreground">
                    Showing <span className="font-medium">{(currentPage - 1) * rowsPerPage + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(currentPage * rowsPerPage, filteredAndSortedData.length)}
                    </span>{' '}
                    of <span className="font-medium">{filteredAndSortedData.length}</span> results
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    
                    <div className="flex gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        // Logic to show context-relevant page numbers
                        let pageNumber;
                        if (totalPages <= 5) {
                          pageNumber = i + 1;
                        } else if (currentPage <= 3) {
                          pageNumber = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNumber = totalPages - 4 + i;
                        } else {
                          pageNumber = currentPage - 2 + i;
                        }
                        
                        return (
                          <Button
                            key={pageNumber}
                            variant={currentPage === pageNumber ? "default" : "outline"}
                            size="sm"
                            onClick={() => setCurrentPage(pageNumber)}
                          >
                            {pageNumber}
                          </Button>
                        );
                      })}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Additional Features */}
              <div className="pt-4 border-t mt-6">
                <h3 className="text-lg font-medium mb-3">Additional Features</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Version Comparison */}
                  <Card className="bg-blue-50 border-blue-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base text-blue-800">Version Comparison</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-blue-600">Compare features and support timelines between different Joomla versions.</p>
                    </CardContent>
                    <CardFooter>
                      <Button variant="outline" size="sm" className="w-full text-blue-600 border-blue-200 hover:bg-blue-100">
                        Coming Soon
                      </Button>
                    </CardFooter>
                  </Card>
                  
                  {/* Migration Assistant */}
                  <Card className="bg-green-50 border-green-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base text-green-800">Migration Assistant</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-green-600">Plan your upgrade path from older versions to the latest release.</p>
                    </CardContent>
                    <CardFooter>
                      <Button variant="outline" size="sm" className="w-full text-green-600 border-green-200 hover:bg-green-100">
                        Coming Soon
                      </Button>
                    </CardFooter>
                  </Card>
                  
                  {/* Security Analysis */}
                  <Card className="bg-amber-50 border-amber-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base text-amber-800">Security Analysis</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-amber-600">View security vulnerabilities fixed in each version update.</p>
                    </CardContent>
                    <CardFooter>
                      <Button variant="outline" size="sm" className="w-full text-amber-600 border-amber-200 hover:bg-amber-100">
                        Coming Soon
                      </Button>
                    </CardFooter>
                  </Card>
                </div>
              </div>
            </CardContent>
            
            <CardFooter className="border-t pt-6 flex flex-col sm:flex-row gap-4 justify-between">
              <div className="text-sm text-muted-foreground">
                <p>Last updated: March 4, 2025</p>
                <p>Data source: Official Joomla release history</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm">Help</Button>
                <Button variant="outline" size="sm">Feedback</Button>
              </div>
            </CardFooter>
          </Card>
          </div>
        );
      };
      
      export default JoomlaVersionTable;